import { HttpProvider } from "../http/http.provider";
import { IAppConfig } from "../http/types/IApplicationConfig";
import { IHttpRequestConfig } from "../http";

import { ServiceEndpointConstants } from "../ServiceUtil";
import { Observable } from "rxjs";
import { share, map } from "rxjs/operators";

import
{
    IEmailProvider,
    IEmailAccountStatus,
    IEmailAccountConnectResult,
    ISendEmailRequest,
    IInboxResult,
    IInboxMessageDetail,
    ICalendarInviteRequest,
    ICalendarInviteResult,
    ICalendarInviteStatusResult
} from "./email-account.interface";

export class EmailAccountDelegate
{
    constructor(private SERVICE_CONFIG: IAppConfig, private http: HttpProvider)
    {
    }

    public getProviders(): Observable<IEmailProvider[]>
    {
        return this.http.get(ServiceEndpointConstants.endpoints.EMAIL_ACCOUNT.PROVIDERS).pipe(
            map((response: any) => response.data as IEmailProvider[]),
            share());
    }

    public getStatus(): Observable<IEmailAccountStatus>
    {
        return this.http.get(ServiceEndpointConstants.endpoints.EMAIL_ACCOUNT.STATUS).pipe(
            map((response: any) => response.data as IEmailAccountStatus),
            share());
    }

    public connect(provider: number): Observable<IEmailAccountConnectResult>
    {
        const config: IHttpRequestConfig = {
            params: {
                provider: provider
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.EMAIL_ACCOUNT.CONNECT, null, config).pipe(
            map((response: any) => response.data as IEmailAccountConnectResult),
            share());
    }

    public disconnect(): Observable<boolean>
    {
        return this.http.post(ServiceEndpointConstants.endpoints.EMAIL_ACCOUNT.DISCONNECT, {}).pipe(
            map((response: any) => (response.data && response.data.success) || true),
            share());
    }

    public getInbox(top: number, skip: number, pageToken?: string): Observable<IInboxResult>
    {
        const params: any = { top: top };
        if (pageToken)
        {
            params.pageToken = pageToken;
        }
        else
        {
            params.skip = skip;
        }
        const config: IHttpRequestConfig = { params: params };
        return this.http.get(ServiceEndpointConstants.endpoints.EMAIL_ACCOUNT.INBOX, null, config).pipe(
            map((response: any) => response.data as IInboxResult),
            share());
    }

    public getInboxMessage(messageId: string): Observable<IInboxMessageDetail>
    {
        const url = ServiceEndpointConstants.endpoints.EMAIL_ACCOUNT.INBOX + "/" + encodeURIComponent(messageId);
        return this.http.get(url).pipe(
            map((response: any) => response.data as IInboxMessageDetail),
            share());
    }

    public replyToMessage(messageId: string, htmlBody: string, replyAll: boolean): Observable<boolean>
    {
        const url = ServiceEndpointConstants.endpoints.EMAIL_ACCOUNT.INBOX + "/" + encodeURIComponent(messageId) + (replyAll ? "/reply-all" : "/reply");
        return this.http.post(url, { htmlBody: htmlBody }).pipe(
            map((response: any) => (response.data && response.data.success) ?? true),
            share());
    }

    public sendEmail(email: ISendEmailRequest): Observable<boolean>
    {
        return this.http.post(ServiceEndpointConstants.endpoints.EMAIL_ACCOUNT.SEND, email).pipe(
            map((response: any) => (response.data && response.data.success) ?? true),
            share());
    }

    public sendCalendarInvite(invite: ICalendarInviteRequest): Observable<ICalendarInviteResult>
    {
        return this.http.post(ServiceEndpointConstants.endpoints.EMAIL_ACCOUNT.CALENDAR_INVITE, invite).pipe(
            map((response: any) => response.data as ICalendarInviteResult),
            share());
    }

    public getCalendarInviteStatus(providerEventId: string): Observable<ICalendarInviteStatusResult>
    {
        const url = ServiceEndpointConstants.endpoints.EMAIL_ACCOUNT.CALENDAR_INVITE + "/" + encodeURIComponent(providerEventId) + "/status";
        return this.http.get(url).pipe(
            map((response: any) => response.data as ICalendarInviteStatusResult),
            share());
    }
}
